import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KEYFRAMES } from "@/lib/brand";
import StudioLogo from "@/components/StudioLogo";
import {
  CheckCircle2, Download, MapPin, Calendar, Sparkles, Loader2,
  Heart, Share2, XCircle, Clock,
} from "lucide-react";

/**
 * iter128 — Public Proposal Page.
 *
 * Mobile-first premium landing that a client opens from a WhatsApp/email
 * share link (`/proposal/{token}`). No auth — the 32-hex token is the
 * capability. Reads `/api/public/proposals/{token}` for the payload, and
 * supports a soft "Accept Proposal" action that hits
 * `/api/public/proposals/{token}/accept`.
 *
 * Aesthetic: warm-luxury editorial — cream + tan cards, generous type
 * scale, subtle warmth, no gradients. Designed to feel like opening a
 * printed proposal folder, not a web form.
 */

// Explicit backend base (bypasses the app's authed api client)
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function inr(n) {
  const v = Number(n || 0);
  if (isNaN(v)) return "₹0";
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function Divider({ children }) {
  return (
    <div className="flex items-center gap-4 my-8 sm:my-12">
      <div className="h-px flex-1 bg-[#e7d5b4]" />
      {children && (
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#8c7a63]">
          {children}
        </div>
      )}
      <div className="h-px flex-1 bg-[#e7d5b4]" />
    </div>
  );
}

export default function PublicProposal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [justAccepted, setJustAccepted] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);

  // iter144 — Detect print mode ONCE at module scope so the /view ping
  // never fires for the headless Playwright PDF renderer.
  const printMode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("print") === "1";

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_BASE}/api/public/proposals/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Proposal not found"));
    // iter144 — fire-and-forget view ping. Skipped during PDF rendering
    // (Playwright appends ?print=1) so studio-side prints don't skew
    // analytics.
    if (!printMode) {
      axios
        .post(`${API_BASE}/api/public/proposals/${token}/view`)
        .catch(() => { /* analytics failure is not user-facing */ });
    }
  }, [token, printMode]);

  const accept = async () => {
    if (!token || accepting) return;
    setAccepting(true);
    try {
      await axios.post(`${API_BASE}/api/public/proposals/${token}/accept`);
      setJustAccepted(true);
      setData((d) => (d ? { ...d, status: "accepted", accepted_at: new Date().toISOString() } : d));
      toast.success("Proposal accepted — thank you!");
    } catch (e) {
      toast.error("Could not accept — please try again");
    } finally {
      setAccepting(false);
    }
  };

  const decline = async () => {
    if (!token || declining) return;
    setDeclining(true);
    try {
      await axios.post(`${API_BASE}/api/public/proposals/${token}/reject`, {
        reason: declineReason.trim(),
      });
      setData((d) => (d ? { ...d, status: "rejected", rejected_at: new Date().toISOString() } : d));
      setShowDecline(false);
      toast.success("Thank you for letting us know");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again");
    } finally {
      setDeclining(false);
    }
  };

  // iter147 — Escape-key closes the Decline modal for keyboard users.
  useEffect(() => {
    if (!showDecline) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !declining) setShowDecline(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showDecline, declining]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#f5efe4] flex items-center justify-center p-6" data-testid="proposal-page-error">
        <div className="max-w-md w-full text-center bg-white/70 border border-[#e7d5b4] rounded-2xl shadow-[0_8px_32px_rgba(140,90,50,0.08)] p-10">
          <div className="mb-4 flex justify-center">
            <StudioLogo size={60} />
          </div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63] mb-3">Proposal unavailable</div>
          <h1 className="font-serif text-2xl text-[#3b2f24] mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            We couldn't open that proposal
          </h1>
          <p className="text-[#7a6853] text-sm mb-6">{err}</p>
          <p className="text-[11px] text-[#8c7a63]/80">
            Please reach out to the studio if you think this is a mistake.
          </p>
        </div>
      </div>
    );
  }
  if (!data) {
    // iter147 — Skeleton-based loading state so the header shell stays
    // stable while the payload streams in (no CLS). Matches the warm
    // luxury palette and respects prefers-reduced-motion.
    return (
      <div className="min-h-screen bg-[#f5efe4]" data-testid="proposal-page-loading" aria-busy="true">
        <div className="max-w-2xl mx-auto px-5 sm:px-10 py-14 space-y-8">
          <div className="flex flex-col items-center gap-4">
            <div className="kb-skeleton w-16 h-16 rounded-full" />
            <div className="kb-skeleton h-3 w-40" />
            <div className="kb-skeleton h-10 w-72 max-w-full" />
            <div className="kb-skeleton h-3 w-56 max-w-full" />
          </div>
          <div className="bg-white/70 border border-[#e7d5b4] rounded-2xl p-6 space-y-3 shadow-[0_8px_32px_rgba(140,90,50,0.08)]">
            <div className="kb-skeleton h-3 w-20 mx-auto" />
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="space-y-2">
                <div className="kb-skeleton h-3 w-16 mx-auto" />
                <div className="kb-skeleton h-4 w-24 mx-auto" />
              </div>
              <div className="space-y-2">
                <div className="kb-skeleton h-3 w-16 mx-auto" />
                <div className="kb-skeleton h-4 w-24 mx-auto" />
              </div>
              <div className="space-y-2">
                <div className="kb-skeleton h-3 w-16 mx-auto" />
                <div className="kb-skeleton h-4 w-24 mx-auto" />
              </div>
            </div>
          </div>
          <div className="bg-white/70 border border-[#e7d5b4] rounded-2xl p-6 space-y-3">
            <div className="kb-skeleton h-4 w-40" />
            <div className="kb-skeleton h-3 w-full" />
            <div className="kb-skeleton h-3 w-11/12" />
            <div className="kb-skeleton h-3 w-9/12" />
          </div>
          <span className="sr-only">Loading proposal…</span>
        </div>
      </div>
    );
  }

  const accepted = data.status === "accepted";
  const rejected = data.status === "rejected";
  const expired = data.status === "expired";
  const pdfUrl = `${API_BASE}${data.pdf_url}`;

  return (
    <div
      className="min-h-screen bg-[#f5efe4] text-[#3b2f24]"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
      data-testid="public-proposal-page"
      data-print-mode={printMode ? "1" : "0"}
    >
      {/* iter133 — Shared brand keyframes (kb-card-hover, kb-fade, kbFadeUp
          etc.) so this page feels one continuous ecosystem with the
          Inquiry Thank-You hero + future Client Portal. */}
      <style>{KEYFRAMES}</style>
      {/* iter129 — Print-mode + @media print styling. Keeps browser view
          exactly as-is while making the PDF export feel like a magazine
          spread: hide the Accept CTA & footer PDF link, respect page
          breaks, force color-preserved backgrounds. */}
      <style>{`
        @media print {
          html, body { background: #f5efe4 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          [data-print-hide="1"] { display: none !important; }
          [data-print-avoid-break="1"] { break-inside: avoid; page-break-inside: avoid; }
        }
        [data-print-mode="1"] [data-print-hide="1"] { display: none !important; }
        [data-print-mode="1"] { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}</style>

      {/* ============= HERO ============= */}
      <header className="px-5 sm:px-10 pt-10 sm:pt-16 pb-10 sm:pb-16 max-w-3xl mx-auto text-center">
        {/* iter142 — Studio logo (auto-resizes, aspect-preserved).
            Falls back to text lockup showing studio name when no logo
            is configured. */}
        <div className="mb-6 flex justify-center" data-testid="proposal-studio-logo">
          <StudioLogo size={80} className="mx-auto max-h-[80px]" />
        </div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63] mb-4">
          {data.studio.name}
        </div>
        <h1
          className="font-serif text-4xl sm:text-6xl leading-tight text-[#3b2f24]"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 500 }}
          data-testid="proposal-hero-title"
        >
          A proposal, crafted with care for
        </h1>
        <div
          className="font-serif text-3xl sm:text-5xl mt-3 text-[#9c7550] italic"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {data.client.couple_name || data.client.name}
        </div>
        <p className="mt-6 text-sm sm:text-base text-[#7a6853] italic max-w-xl mx-auto">
          {data.studio.tagline}
        </p>
      </header>

      {/* ============= EVENT SNAPSHOT ============= */}
      <section className="px-5 sm:px-10 max-w-3xl mx-auto" data-testid="proposal-event-snapshot" data-print-avoid-break="1">
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(140,90,50,0.08)] border border-[#e7d5b4]/60 px-6 sm:px-10 py-8 sm:py-10 kb-card-hover">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#8c7a63] mb-4 text-center">
            Your Story
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center">
            <div>
              <Heart className="w-4 h-4 mx-auto text-[#b18860]" strokeWidth={1.6} />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#8c7a63] mt-2">Event</div>
              <div className="mt-1 text-[#3b2f24] font-medium">{data.event.name}</div>
            </div>
            {(data.event.all_dates?.length > 0 || data.event.primary_date_pretty) && (
              <div>
                <Calendar className="w-4 h-4 mx-auto text-[#b18860]" strokeWidth={1.6} />
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#8c7a63] mt-2">
                  {(data.event.all_dates?.length || 0) > 1 ? "Dates" : "Date"}
                </div>
                {/* iter135 — multi-day support: show every event date on
                    its own line. Single-day proposals still read like the
                    original one-line design because only one row renders. */}
                {data.event.all_dates?.length > 0 ? (
                  <div className="mt-1 space-y-0.5">
                    {data.event.all_dates.map((d) => (
                      <div key={d.date} className="text-[#3b2f24] font-medium">
                        {d.pretty}
                        {d.event_type && (
                          <span className="text-[#8c7a63] text-[12px] italic ml-1">
                            · {d.event_type}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-[#3b2f24] font-medium">{data.event.primary_date_pretty}</div>
                )}
              </div>
            )}
            {data.event.location && (
              <div>
                <MapPin className="w-4 h-4 mx-auto text-[#b18860]" strokeWidth={1.6} />
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#8c7a63] mt-2">Venue</div>
                <div className="mt-1 text-[#3b2f24] font-medium">{data.event.location}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Divider>The Package</Divider>

      {/* ============= LINE ITEMS BY FUNCTION ============= */}
      <section className="px-5 sm:px-10 max-w-3xl mx-auto space-y-8" data-testid="proposal-functions">
        {(data.functions || []).map((fn, i) => (
          <div key={i} className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-6 sm:px-10 py-6 sm:py-8 kb-card-hover" data-print-avoid-break="1" data-testid={`proposal-function-${i}`}>
            {/* iter143 — Event header: DAY N · EVENT NAME · DATE.
                Falls back to legacy "Function N" if event name isn't
                configured (older bookings). */}
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#8c7a63] mb-1">
              {fn.day_label ? fn.day_label : `Function ${i + 1}`}
            </div>
            {fn.event_name ? (
              <>
                <div
                  className="font-serif text-2xl text-[#9c7550]"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  data-testid={`proposal-function-event-${i}`}
                >
                  {fn.event_name}
                </div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#b18860] mb-4 mt-1">
                  {fn.date_pretty || "Package inclusions"}
                </div>
              </>
            ) : (
              <div
                className="font-serif text-2xl text-[#9c7550] mb-4"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {fn.date_pretty || "Package inclusions"}
              </div>
            )}
            <ul className="space-y-2.5">
              {fn.items.map((it, j) => (
                <li key={j} className="flex items-baseline justify-between gap-4 border-b border-dotted border-[#e7d5b4] pb-2.5">
                  <div className="min-w-0">
                    <div className="text-[#3b2f24]">
                      {it.quantity > 1 ? `${it.quantity}× ` : ""}
                      {it.label}
                      {it.is_complimentary && (
                        <span className="ml-2 inline-block text-[9px] tracking-[0.25em] uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-[1px] align-middle">
                          Complimentary
                        </span>
                      )}
                    </div>
                  </div>
                  {/* iter146 — Individual service prices are hidden on the
                      public proposal. The client sees only the Event
                      Subtotal below and the Grand Total at the bottom.
                      Complimentary items still surface their "Included"
                      badge so the freebies are obvious. */}
                  {it.is_complimentary && (
                    <div className="font-mono text-sm shrink-0 text-emerald-700 italic">
                      Included
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex items-baseline justify-between mt-4 pt-3 border-t border-[#e7d5b4]">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#8c7a63]">Subtotal</div>
              <div className="font-mono text-[#3b2f24] font-semibold">{inr(fn.subtotal)}</div>
            </div>
          </div>
        ))}
      </section>

      <Divider>Investment</Divider>

      {/* ============= TOTALS ============= */}
      <section className="px-5 sm:px-10 max-w-3xl mx-auto" data-testid="proposal-totals" data-print-avoid-break="1">
        <div className="bg-[#b18860] text-[#fff4dc] rounded-2xl px-6 sm:px-10 py-8 sm:py-10 kb-card-hover">
          <div className="space-y-2 text-sm sm:text-base">
            <div className="flex justify-between">
              <span className="opacity-80">Subtotal</span>
              <span className="font-mono">{inr(data.totals.subtotal)}</span>
            </div>
            {data.totals.gst_percent > 0 && (
              <div className="flex justify-between">
                <span className="opacity-80">GST ({data.totals.gst_percent}%)</span>
                <span className="font-mono">{inr(data.totals.gst_amount)}</span>
              </div>
            )}
          </div>
          <div className="mt-5 pt-5 border-t border-[#fff4dc]/25 flex flex-col sm:flex-row justify-between gap-2 sm:items-baseline">
            <div className="text-[10px] tracking-[0.35em] uppercase opacity-75">Grand Total</div>
            <div className="font-serif text-3xl sm:text-4xl" style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="proposal-grand-total-public">
              {inr(data.totals.grand_total)}
            </div>
          </div>
          {data.totals.advance_expected > 0 && (
            <div className="mt-4 text-xs sm:text-sm opacity-90 flex flex-col sm:flex-row sm:justify-between gap-1">
              <span>Advance to confirm dates</span>
              <span className="font-mono">{inr(data.totals.advance_expected)}</span>
            </div>
          )}
        </div>
      </section>

      {/* ============= PAYMENT SCHEDULE ============= */}
      {data.payment_schedule?.length > 0 && (
        <>
          <Divider>Payment Schedule</Divider>
          <section className="px-5 sm:px-10 max-w-3xl mx-auto" data-testid="proposal-payment-schedule">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {data.payment_schedule.map((p, i) => (
                <div
                  key={p.type}
                  className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-6 py-6 kb-card-hover"
                  data-print-avoid-break="1"
                >
                  <div className="text-[10px] tracking-[0.35em] uppercase text-[#8c7a63] mb-1">
                    {String(i + 1).padStart(2, "0")} · {p.label}
                  </div>
                  <div
                    className="font-serif text-3xl text-[#9c7550] my-3"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {p.percent}%
                  </div>
                  <div className="font-mono text-sm text-[#3b2f24] font-semibold">
                    {inr(p.amount)}
                  </div>
                  <div className="text-[11px] text-[#8c7a63] mt-3 leading-relaxed">
                    {p.description}
                  </div>
                  {p.due_date_pretty && (
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#b18860] mt-2 font-medium">
                      Due {p.due_date_pretty}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* ============= PAYMENT RESPONSIBILITY (iter137 — Split Payment) ============= */}
      {data.payment_split?.enabled && data.payment_split.parties?.length > 0 && (
        <>
          <Divider>Payment Responsibility</Divider>
          <section
            className="px-5 sm:px-10 max-w-3xl mx-auto"
            data-testid="proposal-payment-split"
          >
            <p className="text-center text-[13px] text-[#8c7a63] mb-6 leading-relaxed">
              The investment above is shared across the following {data.payment_split.parties.length}
              {" "}parties. Each carries their own 30 / 50 / 20 schedule below.
            </p>
            <div className={`grid gap-5 ${data.payment_split.parties.length > 1 ? "sm:grid-cols-2" : "sm:grid-cols-1"}`}>
              {data.payment_split.parties.map((party, pi) => (
                <div
                  key={party.id || pi}
                  className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-5 sm:px-6 py-5 sm:py-6 kb-card-hover"
                  data-print-avoid-break="1"
                  data-testid={`proposal-split-party-${pi}`}
                >
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <div
                      className="font-serif text-xl text-[#9c7550]"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      {party.name}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-[#3b2f24] font-semibold text-lg">₹{Number(party.amount).toLocaleString("en-IN")}</div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#b18860]">
                        {data.payment_split.contract_value > 0
                          ? `${Math.round((party.amount / data.payment_split.contract_value) * 100)}% of total`
                          : ""}
                      </div>
                    </div>
                  </div>
                  {party.notes && (
                    <div className="text-[11px] text-[#8c7a63] italic mb-3">{party.notes}</div>
                  )}
                  <div className="mt-3 pt-3 border-t border-dotted border-[#e7d5b4]">
                    <div className="text-[10px] tracking-[0.35em] uppercase text-[#8c7a63] mb-2">
                      Their schedule
                    </div>
                    <ul className="space-y-2">
                      {(party.schedule || []).map((row, ri) => (
                        <li
                          key={ri}
                          className="flex items-baseline justify-between gap-3 border-b border-dotted border-[#e7d5b4] pb-2 last:border-0 last:pb-0"
                          data-testid={`proposal-split-schedule-${pi}-${ri}`}
                        >
                          <div className="min-w-0">
                            <div className="text-[13px] text-[#3b2f24]">
                              <span className="font-semibold">{row.percent}%</span> · {row.label}
                            </div>
                            {row.due_date_pretty && (
                              <div className="text-[10px] tracking-[0.2em] uppercase text-[#b18860] mt-0.5">
                                Due {row.due_date_pretty}
                              </div>
                            )}
                          </div>
                          <div className="font-mono text-sm text-[#3b2f24] font-semibold shrink-0">
                            ₹{Number(row.amount).toLocaleString("en-IN")}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 text-center text-[10px] tracking-[0.35em] uppercase text-[#8c7a63]">
              Combined obligation
              <span className="mx-2 font-mono text-[13px] text-[#3b2f24]">
                ₹{Number(data.payment_split.total_assigned).toLocaleString("en-IN")}
              </span>
              of ₹{Number(data.payment_split.contract_value).toLocaleString("en-IN")}
              {data.payment_split.balanced ? " ✓" : " (imbalanced)"}
            </div>
          </section>
        </>
      )}

      {/* ============= DELIVERABLES (categorized) ============= */}
      {data.deliverables_groups?.length > 0 && (
        <>
          <Divider>Deliverables</Divider>
          <section className="px-5 sm:px-10 max-w-3xl mx-auto space-y-4" data-testid="proposal-deliverables">
            {data.deliverables_groups.map((g, i) => (
              <div
                key={g.title}
                className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-6 sm:px-10 py-6 sm:py-7 kb-card-hover"
                data-print-avoid-break="1"
                data-testid={`proposal-deliv-${g.title.toLowerCase().replace(/\s+/g,'-')}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63] font-medium">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div
                    className="font-serif text-xl text-[#9c7550]"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {g.title}
                  </div>
                </div>
                <ul className="space-y-2 pl-1">
                  {g.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3 text-[#3b2f24] text-[14px] leading-relaxed">
                      <span className="mt-2 w-1 h-1 rounded-full bg-[#b18860] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        </>
      )}

      {/* ============= FREEFORM STUDIO NOTES (legacy field, still shown) ============= */}
      {data.deliverables && !data.deliverables_groups?.length && (
        <>
          <Divider>Deliverables</Divider>
          <section className="px-5 sm:px-10 max-w-3xl mx-auto" data-testid="proposal-deliverables-legacy">
            <div className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-6 sm:px-10 py-6 sm:py-8">
              <p className="whitespace-pre-line text-[#3b2f24] leading-relaxed">
                {data.deliverables}
              </p>
            </div>
          </section>
        </>
      )}

      {/* ============= NOTES ============= */}
      {data.notes && (
        <>
          <Divider>A Note From The Studio</Divider>
          <section className="px-5 sm:px-10 max-w-3xl mx-auto" data-testid="proposal-notes">
            <div className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-6 sm:px-10 py-6 sm:py-8 italic text-[#5a4a38] kb-card-hover">
              <p className="whitespace-pre-line leading-relaxed">{data.notes}</p>
            </div>
          </section>
        </>
      )}

      {/* ============= TERMS & CONDITIONS ============= */}
      {data.terms?.length > 0 && (
        <>
          <Divider>Terms &amp; Conditions</Divider>
          <section className="px-5 sm:px-10 max-w-3xl mx-auto" data-testid="proposal-terms">
            <div
              className="bg-white rounded-2xl border border-[#e7d5b4]/60 px-6 sm:px-10 py-6 sm:py-8 kb-card-hover"
              data-print-avoid-break="1"
            >
              <ul className="space-y-2.5">
                {data.terms.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#3b2f24] text-[14px] leading-relaxed">
                    <span className="mt-2 w-1 h-1 rounded-full bg-[#b18860] shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}

      <Divider />

      {/* ============= CTA ============= */}
      <section className="px-5 sm:px-10 max-w-2xl mx-auto text-center pb-10" data-testid="proposal-cta" data-print-hide="1">
        {accepted ? (
          <div className="bg-[#f3ede2] border border-[#b18860] rounded-2xl px-6 py-8" data-testid="proposal-accepted">
            <CheckCircle2 className="w-8 h-8 mx-auto text-[#9c7550]" strokeWidth={1.6} />
            <div className="mt-3 text-[10px] tracking-[0.35em] uppercase text-[#8c7a63]">Accepted</div>
            <div className="font-serif text-2xl mt-2 text-[#3b2f24]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {justAccepted ? "Thank you — we can't wait to work with you." : "You've accepted this proposal."}
            </div>
            <p className="mt-3 text-sm text-[#7a6853]">
              Our coordinator will reach out shortly to confirm your dates and next steps.
            </p>
          </div>
        ) : rejected ? (
          <div className="bg-white border border-[#e7d5b4] rounded-2xl px-6 py-8" data-testid="proposal-rejected">
            <XCircle className="w-8 h-8 mx-auto text-[#a04c4c]" strokeWidth={1.6} />
            <div className="mt-3 text-[10px] tracking-[0.35em] uppercase text-[#8c7a63]">Declined</div>
            <div className="font-serif text-2xl mt-2 text-[#3b2f24]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              You've declined this proposal.
            </div>
            <p className="mt-3 text-sm text-[#7a6853]">
              We appreciate you taking the time to review it. Reach out anytime — we'd love to build a package that suits you better.
            </p>
          </div>
        ) : expired ? (
          <div className="bg-white border border-[#e7d5b4] rounded-2xl px-6 py-8" data-testid="proposal-expired">
            <Clock className="w-8 h-8 mx-auto text-[#b18860]" strokeWidth={1.6} />
            <div className="mt-3 text-[10px] tracking-[0.35em] uppercase text-[#8c7a63]">Proposal expired</div>
            <div className="font-serif text-2xl mt-2 text-[#3b2f24]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              This proposal has lapsed.
            </div>
            <p className="mt-3 text-sm text-[#7a6853]">
              Please contact the studio for a refreshed quotation — we'd be delighted to serve you.
            </p>
          </div>
        ) : (
          <>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#8c7a63]">Ready to Begin?</div>
            <h2 className="font-serif text-2xl sm:text-3xl mt-2 text-[#3b2f24]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Let's create something timeless.
            </h2>
            <p className="mt-3 text-sm text-[#7a6853] max-w-lg mx-auto leading-relaxed">
              We're honoured to be a part of your celebration and create memories that
              last a lifetime.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 w-full max-w-md mx-auto sm:max-w-none">
              <button
                type="button"
                onClick={accept}
                disabled={accepting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#b18860] hover:bg-[#9c7550] text-[#fff4dc] px-8 py-3.5 rounded-full transition-colors disabled:opacity-60 kb-tap kb-glow-hover shadow-sm"
                data-testid="proposal-accept-btn"
                aria-label="Accept this proposal"
              >
                {accepting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Sparkles className="w-4 h-4" aria-hidden="true" />}
                <span className="tracking-wider text-sm uppercase">Accept Proposal</span>
              </button>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-[#3b2f24] border border-[#e7d5b4] px-6 py-3 rounded-full hover:border-[#b18860] transition-colors kb-tap"
                data-testid="proposal-cta-download"
                aria-label="Download proposal as PDF"
              >
                <Download className="w-4 h-4" strokeWidth={1.6} aria-hidden="true" />
                <span className="tracking-wider text-xs uppercase">Download PDF</span>
              </a>
              <button
                type="button"
                onClick={() => {
                  const url = typeof window !== "undefined" ? window.location.href : "";
                  if (navigator.share) {
                    navigator.share({ title: "Kahani Studio Proposal", url }).catch(() => {});
                  } else if (navigator.clipboard && url) {
                    navigator.clipboard.writeText(url);
                    toast.success("Proposal link copied");
                  }
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-[#3b2f24] border border-[#e7d5b4] px-6 py-3 rounded-full hover:border-[#b18860] transition-colors kb-tap"
                data-testid="proposal-cta-share"
                aria-label="Share proposal link"
              >
                <Share2 className="w-4 h-4" strokeWidth={1.6} aria-hidden="true" />
                <span className="tracking-wider text-xs uppercase">Share Proposal</span>
              </button>
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowDecline(true)}
                className="text-xs tracking-widest uppercase text-[#8c7a63] hover:text-[#a04c4c] underline underline-offset-4"
                data-testid="proposal-decline-btn"
              >
                Politely decline
              </button>
            </div>
          </>
        )}
      </section>

      {/* iter144 — Decline modal */}
      {showDecline && !accepted && !rejected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 kb-fade-in"
          onClick={() => !declining && setShowDecline(false)}
          data-print-hide="1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="proposal-decline-title"
        >
          <div
            className="bg-[#fdf7ec] border border-[#e7d5b4] rounded-2xl max-w-md w-full p-6 sm:p-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="proposal-decline-modal"
          >
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#8c7a63] mb-2">Politely decline</div>
            <h3 id="proposal-decline-title" className="font-serif text-2xl text-[#3b2f24] mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Not the right fit?
            </h3>
            <p className="text-sm text-[#7a6853] mb-4 leading-relaxed">
              If you'd like to share a reason it will help us serve you better. This is optional.
            </p>
            <textarea
              rows={3}
              className="w-full bg-white border border-[#e7d5b4] rounded-lg px-3 py-2 text-sm text-[#3b2f24] focus:outline-none focus:border-[#b18860]"
              placeholder="Budget, timing, going with someone else…"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              maxLength={500}
              data-testid="proposal-decline-reason"
            />
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDecline(false)}
                disabled={declining}
                className="px-5 py-2 rounded-full text-sm text-[#7a6853] hover:bg-white transition-colors"
                data-testid="proposal-decline-cancel"
              >
                Keep reviewing
              </button>
              <button
                type="button"
                onClick={decline}
                disabled={declining}
                className="inline-flex items-center gap-2 bg-[#a04c4c] hover:bg-[#853f3f] text-white px-6 py-2.5 rounded-full text-sm transition-colors disabled:opacity-60"
                data-testid="proposal-decline-submit"
              >
                {declining ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                <span className="uppercase tracking-wider text-xs">Submit</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter147 — Removed the duplicate "Download printable PDF" link.
           The primary CTA row already has a prominent Download PDF
           action, so surfacing the same link a second time was noise. */}

      {/* ============= META FOOTER ============= */}
      <footer className="bg-[#eae1cf] px-5 sm:px-10 py-8 text-center">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63]">
          {data.studio.name}
        </div>
        <div className="text-[10px] text-[#8c7a63]/80 mt-2">
          Proposal {data.number}{data.date_pretty ? ` · ${data.date_pretty}` : ""} · Valid for {data.validity_days} days
        </div>
      </footer>
    </div>
  );
}
